import { useEffect } from 'react'
import {
  addUnsyncedRequests,
  getS3EntriesUpdatedForOfflineUse,
  getUpdatesOursSelector,
  setS3EntriesUpdatedForOfflineUse,
  updateS3Entries,
} from '../../../features/data/dataSlice'
import { createLogger } from '../../../app/logger'
import { CheckinEntryUpdate, S3CheckinEntry } from '../../../app/types'
import LocalForage from '../../../app/localForage'
import { useEventId } from '../../../app/location'
import useAppDispatch from '../../../hooks/useAppDispatch'
import useAppSelector from '../../../hooks/useAppSelector'
import { getOnlineSelector } from '../../network/networkSlice'
import { useTranslation } from 'react-i18next'
import { useUnsyncedUpdates } from '../../data/hooks/useUnsyncedUpdates'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'CheckinLocalForage'
})

export default function CheckinLocalForage() {
  const dispatch = useAppDispatch()
  const eventId = useEventId()
  const online = useAppSelector(getOnlineSelector)
  const { t } = useTranslation()
  const entriesUpdatedForOfflineUse = useAppSelector(getS3EntriesUpdatedForOfflineUse)
  const unsyncedUpdates = useUnsyncedUpdates()
  const numRequests = unsyncedUpdates.length
  const updatesOurs = useAppSelector(getUpdatesOursSelector)

  const leaveMessage = t('You have unsaved changes. Are you sure you want to leave?')

  useEffect(() => {
    LocalForage.instance().get<CheckinEntryUpdate>('unsyncedChanges', eventId)
      ?.then(updates => {
        if (updates?.length) {
          logger.info(`Successfully fetched items from browser storage, Table: unsyncedChanges, EventId: ${eventId}`)
          logger.debug(updates, 'UNSYNCED CHANGES')
          dispatch(
            addUnsyncedRequests(
              updates.map(change => ({
                bib: change.bib,
                checkedIn: change.checkedIn,
                checkInTime: change.checkInTime,
                entryId: change.entryId,
                lastModifiedTime: change.lastModifiedTime,
                raceId: change.raceId,
                regChoiceName: change.regChoiceName,
                teamId: change.teamId,
                teamShareBib: change.teamShareBib,
                teamType: change.teamType,
              }))
            )
          )
        }
      })
  }, [
    dispatch,
    eventId,
  ])

  useEffect(() => {
    if (!online && !entriesUpdatedForOfflineUse) {
      LocalForage.instance().get<S3CheckinEntry>('entries', eventId)
        ?.then(s3Entries => {
          if (s3Entries?.length) {
            dispatch(updateS3Entries(s3Entries))
            dispatch(setS3EntriesUpdatedForOfflineUse(true))
            logger.info(`Successfully fetched items from browser storage, Table: entries, EventId: ${eventId}`)
          }
        })
    }
  }, [
    dispatch,
    eventId,
    online,
    entriesUpdatedForOfflineUse,
  ])

  useEffect(() => {
    logger.info('Number of requests', { numRequests })
    LocalForage.instance().set('kioskChanges', eventId, updatesOurs)
      .then((isStored) => {
        if (isStored) {
          logger.info(`kioskChanges have been successfully saved for EventID: ${eventId}`)
        }
      })
    if (numRequests) {
      if (online) {
        window.onbeforeunload = () => { return leaveMessage }
      }
      else {
        LocalForage.instance().set('unsyncedChanges', eventId, unsyncedUpdates)
          .then((isStored) => {
            if (isStored) {
              logger.info(`unsyncedChanges have been successfully saved for offline use for EventID: ${eventId}`)
            }
          })
      }
    }
    else window.onbeforeunload = null

    return () => { window.onbeforeunload = null }
  }, [
    dispatch,
    eventId,
    leaveMessage,
    numRequests,
    online,
    unsyncedUpdates,
    updatesOurs,
  ])

  return <></>
}
